'use client';

import React from 'react';
import Icon from '@/components/ui/AppIcon';

interface CalendarHeaderProps {
  currentView: 'month' | 'week' | 'day';
  currentDate: Date;
  onViewChange: (view: 'month' | 'week' | 'day') => void;
  onNavigate: (direction: 'prev' | 'next' | 'today') => void;
}

const CalendarHeader: React.FC<CalendarHeaderProps> = ({
  currentView,
  currentDate,
  onViewChange,
  onNavigate,
}) => {
  const formatDate = () => {
    const options: Intl.DateTimeFormatOptions = 
      currentView === 'month' 
        ? { month: 'long', year: 'numeric' }
        : currentView === 'week'
        ? { month: 'short', day: 'numeric', year: 'numeric' }
        : { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' };
    
    return currentDate.toLocaleDateString('en-US', options);
  };

  return (
    <div className="bg-card rounded-lg shadow-sm p-6 mb-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        {/* Navigation Controls */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => onNavigate('today')}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md font-cta text-sm font-bold hover:scale-105 transition-transform duration-300"
          >
            Today
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onNavigate('prev')}
              className="p-2 rounded-md hover:bg-muted transition-colors duration-200"
              aria-label="Previous period"
            >
              <Icon name="ChevronLeftIcon" size={20} />
            </button>
            <button
              onClick={() => onNavigate('next')}
              className="p-2 rounded-md hover:bg-muted transition-colors duration-200"
              aria-label="Next period"
            >
              <Icon name="ChevronRightIcon" size={20} />
            </button>
          </div>
          <h2 className="text-xl font-headline font-bold text-foreground">
            {formatDate()}
          </h2>
        </div>

        {/* View Switcher */}
        <div className="flex items-center gap-2 bg-muted rounded-md p-1">
          <button
            onClick={() => onViewChange('month')}
            className={`px-4 py-2 rounded-md font-body text-sm font-medium transition-all duration-200 ${
              currentView === 'month' ?'bg-card text-primary shadow-sm' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            Month
          </button>
          <button
            onClick={() => onViewChange('week')}
            className={`px-4 py-2 rounded-md font-body text-sm font-medium transition-all duration-200 ${
              currentView === 'week' ?'bg-card text-primary shadow-sm' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            Week
          </button>
          <button
            onClick={() => onViewChange('day')}
            className={`px-4 py-2 rounded-md font-body text-sm font-medium transition-all duration-200 ${
              currentView === 'day' ?'bg-card text-primary shadow-sm' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            Day
          </button>
        </div>
      </div>
    </div>
  );
};

export default CalendarHeader;